import { listMatrixUserEmailsByRoleName } from "./commercialRoleAccess";
import { sameOrgMailbox } from "./userMatrixStorage";

/** Distinct chip colors for crew report events (crew-reports-only calendar view). */
export const CREW_REPORT_COLORS = [
  "#0d9488",
  "#0891b2",
  "#2563eb",
  "#7c3aed",
  "#059669",
  "#ca8a04",
  "#dc2626",
  "#be185d",
  "#4f46e5",
  "#d97706",
  "#9333ea",
  "#ea580c",
];

export function normalizeCrewReporterKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function reporterLocalPart(value) {
  const key = normalizeCrewReporterKey(value);
  if (!key) return "";
  return key.split("@")[0] || key;
}

/** Known Construction Lead emails from the User Matrix. */
export function listKnownConstructionLeadEmails() {
  return listMatrixUserEmailsByRoleName("Construction Lead");
}

/** Map legacy reported_by strings to a canonical Construction Lead email key. */
export function canonicalConstructionLeadKey(value, knownEmails = null) {
  const key = normalizeCrewReporterKey(value);
  if (!key || key === "unknown") return key;
  const known = knownEmails || listKnownConstructionLeadEmails();

  for (const email of known) {
    const canon = normalizeCrewReporterKey(email);
    if (!canon) continue;
    if (key === canon || sameOrgMailbox(key, canon)) return canon;
    if (!key.includes("@") && reporterLocalPart(canon) === key) return canon;
    if (formatCrewReporterLabel(key) === formatCrewReporterLabel(canon)) return canon;
  }

  if (key.includes("@")) {
    for (const email of known) {
      if (sameOrgMailbox(key, email)) return normalizeCrewReporterKey(email);
    }
  }

  return key;
}

export function formatCrewReporterLabel(email) {
  const normalized = normalizeCrewReporterKey(email);
  if (!normalized || normalized === "unknown") return "Unknown construction lead";
  const local = normalized.split("@")[0] || normalized;
  const pretty = local.replace(/[._-]+/g, " ").trim();
  if (!pretty) return normalized;
  return pretty
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function crewReporterInitials(email) {
  const label = formatCrewReporterLabel(email);
  if (label === "Unknown construction lead") return "?";
  const parts = label.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return label.slice(0, 2).toUpperCase();
}

/**
 * Classify a daily progress row as crew vs inspector.
 * Uses stamped report_source when present; otherwise matches reported_by to Construction Leads.
 */
export function classifyDailyProgressReportSource(report) {
  const stamped = String(report?.report_source || "")
    .trim()
    .toLowerCase();
  if (stamped === "crew") return "crew";
  if (stamped === "inspector") return "inspector";

  const reportedBy = String(report?.reported_by || "").trim();
  if (!reportedBy) return "inspector";

  const knownLeads = listKnownConstructionLeadEmails();
  if (!knownLeads.length) return "inspector";
  const key = normalizeCrewReporterKey(reportedBy);
  for (const email of knownLeads) {
    const canon = normalizeCrewReporterKey(email);
    if (!canon) continue;
    if (key === canon || sameOrgMailbox(key, canon)) return "crew";
    if (!key.includes("@") && reporterLocalPart(canon) === key) return "crew";
  }
  return "inspector";
}

export function resolveReportConstructionLeadKey(report) {
  const reportedBy = canonicalConstructionLeadKey(report?.reported_by);
  if (reportedBy) return reportedBy;
  return "unknown";
}

export function isCrewReportsOnlyFilter(filters) {
  return (
    filters?.crewReports === true &&
    filters?.inspectorReports === false &&
    filters?.construction === false &&
    filters?.splicing === false &&
    filters?.networking === false
  );
}

export function listTrackedCrewReporterEmails(extraEmails = []) {
  const known = listKnownConstructionLeadEmails();
  const seen = new Set();
  const out = [];
  for (const email of [...known, ...extraEmails]) {
    const key = canonicalConstructionLeadKey(email, known);
    if (!key || key === "unknown" || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

export function buildCrewReporterColorMap(emails) {
  const map = new Map();
  emails.forEach((email, index) => {
    map.set(normalizeCrewReporterKey(email), CREW_REPORT_COLORS[index % CREW_REPORT_COLORS.length]);
  });
  return map;
}

export function applyCrewColorsToEvents(events, colorMap) {
  return events.map((event) => {
    if (event.eventType !== "crewReports") return event;
    const leadKey = normalizeCrewReporterKey(event.constructionLeadKey);
    const color = colorMap.get(leadKey) || CREW_REPORT_COLORS[0];
    return {
      ...event,
      color,
      typeShort: crewReporterInitials(leadKey),
      constructionLeadLabel: formatCrewReporterLabel(leadKey),
    };
  });
}
